import phoneCountryList from '../data/phoneCountryList';

/** ISO2 → exact `countryName` when Intl name ≠ list name or dial code is shared. */
const ISO2_PREFERRED_NAME = {
  US: 'United States of America',
  CA: 'Canada',
  AU: 'Australia',
  GB: 'England',
  UK: 'England',
  PK: 'Pakistan',
  IN: 'India',
  BD: 'Bangladesh',
  AE: 'United Arab Emirates',
  RU: 'Russia',
  KR: 'South Korea',
  KP: 'North Korea',
  TW: 'Taiwan',
  HK: 'Hong Kong',
  MO: 'Macau',
  CD: 'Democratic Republic of the Congo',
  CG: 'Republic of the Congo',
  LA: 'Laos',
  BO: 'Bolivia (Plurinational State of)',
  VE: 'Venezuela (Bolivarian Republic of)',
  TZ: 'Tanzania',
  FM: 'Federated States of Micronesia',
  MD: 'Moldova',
  MK: 'North Macedonia',
  PS: 'State of Palestine',
  CI: 'Ivory Coast',
};

let regionDisplayNames;

function getCountryNameFromIso(iso) {
  if (!iso || iso.length !== 2) return null;
  const upper = iso.toUpperCase();
  if (ISO2_PREFERRED_NAME[upper]) {
    return ISO2_PREFERRED_NAME[upper];
  }
  try {
    if (!regionDisplayNames) {
      regionDisplayNames = new Intl.DisplayNames(['en'], { type: 'region' });
    }
    return regionDisplayNames.of(upper) || null;
  } catch {
    return null;
  }
}

export const FALLBACK_PHONE_COUNTRY =
  phoneCountryList.find((c) => c.countryName === 'Australia') || phoneCountryList[0];

function normalizeCallingCode(code) {
  if (!code) return null;
  const trimmed = String(code).trim();
  if (!trimmed) return null;
  return trimmed.startsWith('+') ? trimmed : `+${trimmed}`;
}

function findByCountryName(name) {
  if (!name) return null;
  const nameLower = String(name).toLowerCase();
  const exact = phoneCountryList.find((c) => c.countryName.toLowerCase() === nameLower);
  if (exact) return exact;

  return (
    phoneCountryList.find(
      (c) =>
        c.countryName.toLowerCase().includes(nameLower) ||
        nameLower.includes(c.countryName.toLowerCase())
    ) || null
  );
}

function findByIso2(iso) {
  if (!iso) return null;
  const upper = iso.toUpperCase();
  const preferredName = ISO2_PREFERRED_NAME[upper] || getCountryNameFromIso(upper);
  return findByCountryName(preferredName);
}

/**
 * Resolve a list entry from geo API fields.
 */
export function findPhoneCountryFromGeo(geo) {
  if (!geo) return null;

  const iso = geo.iso2?.toUpperCase();

  const fromIso = findByIso2(iso);
  if (fromIso) return fromIso;

  const fromName = findByCountryName(geo.countryName);
  if (fromName) return fromName;

  const dial = normalizeCallingCode(geo.callingCode);
  if (dial) {
    const matches = phoneCountryList.filter((c) => c.countryCode === dial);
    if (matches.length === 1) return matches[0];
    if (iso) {
      const fromIsoDial = matches.find((c) => {
        const listIsoName = ISO2_PREFERRED_NAME[iso] || getCountryNameFromIso(iso);
        return listIsoName && c.countryName === listIsoName;
      });
      if (fromIsoDial) return fromIsoDial;
    }
    if (matches.length > 0) return matches[0];
  }

  return null;
}
